import {computed} from 'vue';
import {useState} from 'nuxt/app';
import {useRoute, useRouter} from 'vue-router';
import {routes} from "~/constants/routes";
import {useFetchWithJwt} from '~/composables/useFetchWithJwt';
import {useHandleCode} from "~/composables/useHandleCode";
import {usePagination} from "~/composables/usePagination";
import {ApiResponse, ApiResponseWithPage, PageData} from '~/types/response'
import {
	BoardData,
	BoardDetailResponse,
	BoardResultResponse,
	CreateBoardRequest,
	UpdateBoardRequest
} from '@/types/board';

export function useBoardData() {
    const {fetchWithJwt} = useFetchWithJwt();
    const {handleErrorCode, handleSuccessCode} = useHandleCode();

    const customPaginationKey = window.location.pathname;//페이지 키 값을 경로로 지정
    const {setParams, setFetchFunction, updatePagination} = usePagination(customPaginationKey);

    const route = useRoute();
    const router = useRouter();

    const {lang} = useLanguage();

    // 오늘 날짜
    const currentDate = new Date().toISOString().split('T')[0];
    // 페이지 id 가져오기
    const boardRouteId = route.query.id ? Number(route.query.id) : null;

    const boardArrayList = useState<BoardData[]>('boardArrayList', () => []);

    const boardId = useState<number>('boardId', () => 0);
    const boardTitle = useState<string>('boardTitle', () => '');
    const boardContent = useState<string>('boardContent', () => '');
    const boardContentHtml = useState<string>('boardContentHtml', () => '');
    const boardUserId = useState<string>('boardUserId', () => '-');
    const boardCreateDate = useState<string>('boardCreateDate', () => '-');

    // 공지 등록용
    const boardRegTitle = useState<string>('boardRegTitle', () => '');
    const boardRegContent = useState<string>('boardRegContent', () => '');
    const boardType = useState<string>('boardType', () => 'NOTICE');
    const boardState = useState<string>('boardState', () => '');
    const boardIsPinned = useState<number>('boardIsPinned', () => 0);
    const boardIsPushEnabled = useState<number>('boardIsPushEnabled', () => 0);
    const boardIsPopupOpened = useState<number>('boardIsPopupOpened', () => 0);
    const boardStartDate = useState<string>('boardStartDate', () => currentDate);
    const boardEndDate = useState<string>('boardEndDate', () => '');

    const fetchBoardDetail = async (id: number): Promise<ApiResponse<BoardDetailResponse>> => {
        return await fetchWithJwt(`/board/detail/${id}`, {
            method: 'GET',
        }) as ApiResponse<BoardDetailResponse>;
    };

    const fetchBoardList = async (params: Record<string, any>): Promise<ApiResponseWithPage<BoardData>> => {
        return (await fetchWithJwt('/board/search', {
            method: 'POST',
            body: JSON.stringify({
                page: params.page,
                size: params.size,
                ...params,
            }),
        })) as ApiResponseWithPage<BoardData>
    };

    const fetchCreateBoardItem = async (body: CreateBoardRequest): Promise<ApiResponse<BoardResultResponse>> => {
        return await fetchWithJwt('/board/create', {
            method: 'PUT',
            body: JSON.stringify(body),
        }) as ApiResponse<BoardResultResponse>;
    };

    const fetchUpdateBoardItem = async (body: UpdateBoardRequest): Promise<ApiResponse<BoardResultResponse>> => {
        return await fetchWithJwt('/board/update', {
            method: 'POST',
            body: JSON.stringify(body),
        }) as ApiResponse<BoardResultResponse>;
    };

    const fetchDeleteBoardItem = async (id: number): Promise<ApiResponse<BoardResultResponse>> => {
        return await fetchWithJwt(`/board/delete/${id}`, {
            method: 'DELETE',
        }) as ApiResponse<BoardResultResponse>;
    };

    /**
     * 오늘 기준으로 노출 가능한 공지사항 목록 조회 (start_date ≤ today ≤ end_date)
     */
    const fetchEffectiveNoticeList = async (
        params: Record<string, any>
    ): Promise<ApiResponseWithPage<BoardData>> => {
        return (await fetchWithJwt('/board/new_notice', {
            method: 'POST',
            body: JSON.stringify({
                ...params
            })
        })) as ApiResponseWithPage<BoardData>;
    };


    // 공지 리스트 조회
    const handlerBoardList = async (params: Record<string, any>): Promise<PageData<BoardData>> => {
        try {
            const response = await fetchBoardList(params);
            if (response?.result === 'success') {
                boardArrayList.value = response.data?.content.map((item, index) => {
                    return {
                        ...item,
                        index: (params.page * params.size) + index + 1,
                        createdDatetime: item.createdDatetime.split("T")[0]
                    }
                })

                await updatePagination(response.data as PageData<BoardData>);
                setParams(params);
                setFetchFunction(handlerBoardList);

                return response.data as PageData<BoardData>;
            } else {
                handleErrorCode(response.message);
                return {content: [], totalPages: 0, totalElements: 0, number: 0, size: 0, first: false, last: false};
            }
        } catch (error) {
            console.error('Error fetching beta data:', error);
            handleErrorCode('E50001');
            return {content: [], totalPages: 0, totalElements: 0, number: 0, size: 0, first: false, last: false};
        }
    }

    // 공지 상세 조회
    const handlerBoardDetail = async (boardRouteId: any) => {
        try {
            const response = await fetchBoardDetail(boardRouteId);
            if (response && response.result === "success") {
                boardTitle.value = response.data.boardTitle;
                boardContent.value = response.data.boardContent;
                boardContentHtml.value = response.data.boardContentHtml;
                boardUserId.value = response.data.createdUserId;
                boardCreateDate.value = response.data.createdDatetime.split("T")[0];
                boardId.value = response.data.id;
            } else {
                handleErrorCode(response.message);
                console.warn("❌ 게시글 데이터를 찾을 수 없습니다.");
            }
        } catch (error) {
            handleErrorCode('E50000');
            console.error("❌ 게시글 불러오기 실패:", error);
        }
    }

    // 공지 수정
    const handlerBoardUpdate = async () => {
        const payload = {
            boardTitle: boardTitle.value,
            boardContent: boardContent.value,
        };

        try {
            const response = await fetchUpdateBoardItem({id: boardId.value, ...payload});
            if (response && response.result === "success") {
                handleSuccessCode('S00003');
                // 페이지 이동
                router.push({path: '/management/board/board_page'})
            } else if (response && response.result === "fail") {
                // 에러 코드 처리
                handleErrorCode(response.message);
            }
        } catch (error) {
            handleErrorCode('E50000');
            console.error('게시글 수정 실패:', error);
        }
    }

    // 공지 삭제
    const handlerBoardDelete = async (boardRouteId: any) => {
        try {
            const response = await fetchDeleteBoardItem(boardRouteId);
            if (response && response.result === "success") {
                handleSuccessCode('S00004');
                // 페이지 이동
                router.push({path: '/management/board/board_page'})
            } else if (response && response.result === "fail") {
                // 에러 코드 처리
                handleErrorCode(response.message);
            }
        } catch (error) {
            handleErrorCode('E50000');
            console.error("❌ 삭제 중 오류 발생:", error);
        }
    }

    // 공지 생성
    const handlerBoardCreate = async () => {
        const payload: CreateBoardRequest = {
            boardType: boardType.value,
            state: boardState.value,
            boardTitle: boardRegTitle.value,
            boardContent: boardRegContent.value,
            boardContentHtml: `<pre>${boardRegContent.value}</pre>`,
            isPinned: boardIsPinned.value,
            isPushEnabled: boardIsPushEnabled.value,
            isPopupOpen: boardIsPopupOpened.value,
            startDate: boardStartDate.value,
            endDate: boardEndDate.value,
        }

        try {
            const response = await fetchCreateBoardItem(payload);
            if (response && response.result === "success") {
                handleSuccessCode('S00002');
                // 페이지 이동
                router.push({path: '/management/board/board_page'})
            } else if (response && response.result === "fail") {
                // 에러 코드 처리
                handleErrorCode(response.message);
            }
        } catch (error) {
            console.error('게시글 등록 실패:', error);
        }
    }

    // 오늘 기준으로 노출 가능한 공지사항 목록 조회
    const handlerEffectiveNoticeList = async (params: Record<string, any>): Promise<void> => {
        try {
            const response = await fetchEffectiveNoticeList(params);
            if (response && response.result === "success") {
                const content = response.data?.content ?? [];
                const filledContent = [...content]; // 원본 복사

                // 전체 데이터 개수를 params.size로 나눈 나머지를 구합니다.
                const mod = content.length % params.size;

                // 나머지가 0이 아니면, 한 묶음(페이지) 분량만큼 빈 아이템을 채웁니다.
                if (mod > 0) {
                    const remaining = params.size - mod;

                    const emptyBoard: BoardData = {
                        id: -1,
                        boardType: '',
                        state: '',
                        boardTitle: '-',
                        boardContent: '',
                        boardContentHtml: '',
                        isPinned: 0,
                        isPushEnabled: 0,
                        isPopupOpen: 0,
                        startDate: '',
                        endDate: '',
                        createdUserId: 0,
                        createdDatetime: '',
                        createdCompanyId: 0,
                        updatedUserId: 0,
                        updatedDatetime: '',
                        updatedCompanyId: 0,
                        page: 0,
                        size: 0
                        // 필요한 필드가 더 있으면 추가
                    };

                    for (let i = 0; i < remaining; i++) {
                        filledContent.push({...emptyBoard});
                    }
                }

                boardArrayList.value = filledContent.map((item, index) => {
                    return {
                        ...item,
                        index: (params.page * params.size) + index + 1,
                        createdDatetime: item.createdDatetime.split("T")[0]
                    }
                })

                await updatePagination(response.data as PageData<BoardData>);
                setParams(params);
                setFetchFunction(handlerEffectiveNoticeList);
            } else {
                handleErrorCode(response.message);
            }
        } catch (error) {
            console.error('Error fetching effective notice data:', error);
            handleErrorCode('E50001');
        }
    }

    // route.path 비교
    const isBoardDetail = computed(() => route.path === routes.boardDetail);
    const isBoardRegister = computed(() => route.path === routes.boardRegister);

    // 초기화 다이얼로그
    const dataResetDialog = () => {
        if (isBoardDetail.value) {
            boardTitle.value = '';
            boardContent.value = '';
        } else if (isBoardRegister.value) {
            boardRegTitle.value = '';
            boardRegContent.value = '';
        }
    }

    // 버튼 활성화
    const isBtnDisabled = computed(() => {
        if (isBoardDetail.value) {
            return !boardTitle.value || !boardContent.value
        } else if (isBoardRegister.value) {
            return !boardRegTitle.value || !boardRegContent.value
        }
    })

    // 테이블 헤더
    const boardGridHeader = computed(() => [
        {title: 'No', key: 'index', sortable: true, width: 100},
        {title: lang.board('boardTableHeader.title'), key: 'boardTitle', sortable: true,},
        {title: lang.board('boardTableHeader.createdBy'), key: 'createdUserName', sortable: true,},
        {title: lang.board('boardTableHeader.createdDate'), key: 'createdDatetime', sortable: true,},
    ])

    return {
        fetchBoardList,
        fetchBoardDetail,
        fetchCreateBoardItem,
        fetchUpdateBoardItem,
        fetchDeleteBoardItem,

        handlerBoardList,   // 공지 리스트 조회
        handlerBoardDetail, // 공지 상세 조회
        handlerBoardDelete, // 공지 삭제
        handlerBoardUpdate, // 공지 수정
        handlerBoardCreate, // 공지

        handlerEffectiveNoticeList, // 오늘 노출될 공지 리스트 조회

        dataResetDialog,

        boardRouteId,
        boardArrayList,
        boardTitle,
        boardContent,
        boardContentHtml,
        boardUserId,
        boardCreateDate,

        boardType,
        boardRegTitle,
        boardRegContent,
        boardState,
        boardIsPinned,
        boardIsPopupOpened,
        boardStartDate,
        boardEndDate,
        // boardSearchStartDate,
        // boardSearchEndDate,

        boardGridHeader,    // 테이블 헤더
        isBtnDisabled,      // 버튼 활성화

        route,
        router,
    }
}